/* Geschenke-Shop – kleine Bewegungen und Hilfen, die überall gelten.
   - Abschnitte beim Scrollen einblenden (IntersectionObserver, Rückfall nach 1500 ms)
   - <gs-video>: Pause-Knopf, pausiert außerhalb des Bildes
   - <gs-countdown>: echter Bestellschluss, Minutentakt, startet nie neu */

(() => {
  const root = document.documentElement;
  const motionOk = root.classList.contains('gs-motion');

  /* ---------- Einblenden ---------- */

  function initReveal() {
    const items = document.querySelectorAll('[data-gs-reveal]:not(.is-visible)');
    if (!items.length) return;

    const showAll = () => items.forEach((el) => el.classList.add('is-visible'));

    if (!motionOk || root.dataset.gsReveal !== 'true' || !('IntersectionObserver' in window)) {
      showAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach((el) => observer.observe(el));
    window.setTimeout(showAll, 1500);
  }

  /* ---------- Bilder „hängen sich" an die Wand (data-gs-hang) ----------
     Nur Bewegung, nie verborgen: Ohne JS/Bewegung bleibt alles an Ort und Stelle.
     is-armed setzt die Startlage, is-hung beim ersten Sichtbarwerden die Endlage. */

  function initHang() {
    const lists = document.querySelectorAll('[data-gs-hang]:not(.is-armed)');
    if (!lists.length || !motionOk || root.dataset.gsReveal !== 'true' || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          io.unobserve(el);
          window.requestAnimationFrame(() => el.classList.add('is-hung'));
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
    lists.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9 && r.bottom > 0) return; /* schon im Bild: nicht wackeln */
      el.classList.add('is-armed');
      io.observe(el);
    });
  }

  /* ---------- Video mit Pause-Knopf ---------- */

  class GsVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.gs-media__pause');
      if (!this.video || !this.button) return;

      this.userPaused = !motionOk;
      if (this.userPaused) this.video.pause();
      this.#render();

      this.button.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.userPaused) this.video.pause();
        else this.video.play().catch(() => {});
        this.#render();
      });

      /* Ton-Knopf (nur bei Beispielvideos mit Sprache): Ton an = von vorn, sichtbar weiterlaufen */
      this.sound = this.querySelector('.gs-media__sound');
      this.sound?.addEventListener('click', () => {
        const on = this.video.muted;
        this.video.muted = !on;
        if (on) {
          this.video.currentTime = 0;
          this.userPaused = false;
          this.video.play().catch(() => {});
          this.#render();
        }
        this.sound.textContent = on ? this.sound.dataset.labelOff : this.sound.dataset.labelOn;
        this.sound.setAttribute('aria-pressed', String(on));
      });

      this.observer = new IntersectionObserver(([entry]) => {
        if (!entry || this.userPaused) return;
        if (entry.isIntersecting) this.video.play().catch(() => {});
        else {
          this.video.pause();
          if (this.sound && !this.video.muted) this.sound.click();
        }
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    #render() {
      const label = this.userPaused ? this.button.dataset.labelPlay : this.button.dataset.labelPause;
      const icon = this.userPaused ? this.button.dataset.iconPlay : this.button.dataset.iconPause;
      if (icon) {
        this.button.textContent = icon;
        this.button.setAttribute('aria-label', label || '');
      } else {
        this.button.textContent = label || '';
      }
      this.button.setAttribute('aria-pressed', String(this.userPaused));
    }
  }

  if (!customElements.get('gs-video')) customElements.define('gs-video', GsVideo);

  /* ---------- Endlos-Bewegung nur, solange der Bereich im Bild ist ---------- */

  const initLive = () => {
    const els = document.querySelectorAll('[data-gs-live]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-live'));
      return;
    }
    const io = new IntersectionObserver((entries) =>
      entries.forEach((e) => e.target.classList.toggle('is-live', e.isIntersecting))
    );
    els.forEach((el) => io.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initLive);
  else initLive();

  /* ---------- Gezeichneter Weihnachtsmann: winkt beim Erscheinen und beim Zeigen ---------- */

  class GsSantaFigur extends HTMLElement {
    connectedCallback() {
      if (!motionOk || document.documentElement.dataset.gsMascot === 'false') return;
      const flash = (cls, ms) => {
        if (this.classList.contains(cls)) return;
        this.classList.add(cls);
        window.setTimeout(() => this.classList.remove(cls), ms);
      };
      const wave = () => flash('is-waving', 2100);
      this.addEventListener('pointerenter', wave);
      this.addEventListener('click', () => {
        wave();
        flash('is-flipping', 1900);
        flash('is-talking', 1600);
      });
      if (!('IntersectionObserver' in window)) return;
      let greeted = false;
      new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          /* Endlos-Bewegung nur, solange die Figur zu sehen ist */
          this.classList.toggle('is-live', entry.isIntersecting);
          if (entry.intersectionRatio >= 0.6 && !greeted) {
            greeted = true;
            wave();
          }
        },
        { threshold: [0, 0.6] }
      ).observe(this);
    }
  }

  if (!customElements.get('gs-santa-figur')) customElements.define('gs-santa-figur', GsSantaFigur);

  /* ---------- Countdown bis Bestellschluss ---------- */

  /** Wandelt „JJJJ-MM-TT HH:MM" in deutscher Zeit (Europe/Berlin) in ein Date. */
  function berlinDate(text) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/.exec(String(text).trim());
    if (!m) return null;
    const [, y, mo, d, h, mi] = m.map(Number);
    let guess = Date.UTC(y, mo - 1, d, h - 1, mi);
    const fmt = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    for (let i = 0; i < 2; i++) {
      const [bh, bm] = fmt.format(new Date(guess)).split(':').map(Number);
      const diff = (h * 60 + mi - (bh * 60 + bm)) * 60000;
      if (diff === 0) break;
      guess += diff;
    }
    return new Date(guess);
  }

  class GsCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = berlinDate(this.dataset.deadline || '');
      this.output = this.querySelector('[data-gs-countdown-output]');
      if (!this.deadline || !this.output) return;
      this.tick();
      if (root.dataset.gsCountdown !== 'true') return;
      this.timer = window.setInterval(() => this.tick(), 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const ms = this.deadline.getTime() - Date.now();
      if (ms <= 0) {
        this.classList.add('is-expired');
        this.output.textContent = this.dataset.afterText || '';
        window.clearInterval(this.timer);
        return;
      }
      if (root.dataset.gsCountdown !== 'true') return;
      const minutes = Math.floor(ms / 60000);
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      const mins = minutes % 60;
      const template = days > 0 ? this.dataset.templateDays : this.dataset.templateHours;
      this.output.textContent = (template || '')
        .replace('[days]', String(days))
        .replace('[hours]', String(hours))
        .replace('[minutes]', String(mins));
    }
  }

  if (!customElements.get('gs-countdown')) customElements.define('gs-countdown', GsCountdown);

  /* ---------- In den Warenkorb: Geschenk springt im Bogen zum Warenkorb ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
    if (!button || !motionOk || !('animate' in Element.prototype)) return;
    const form = button.closest('form') || (button.getAttribute('form') && document.getElementById(button.getAttribute('form')));
    if (form instanceof HTMLFormElement && !form.checkValidity()) return;
    const cart = document.querySelector('.header-actions__cart-icon');
    if (!cart) return;
    const from = button.getBoundingClientRect();
    const to = cart.getBoundingClientRect();
    const gift = document.createElement('span');
    gift.className = 'gs-gift-fly';
    gift.textContent = '🎁';
    gift.setAttribute('aria-hidden', 'true');
    document.body.append(gift);
    const x0 = from.left + from.width / 2 - 14;
    const y0 = from.top + from.height / 2 - 14;
    const dx = to.left + to.width / 2 - 14 - x0;
    const dy = to.top + to.height / 2 - 14 - y0;
    const lift = Math.min(-80, dy * 0.5 - 60);
    gift.style.left = `${x0}px`;
    gift.style.top = `${y0}px`;
    const flight = gift.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${lift}px) scale(1.25)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.5)`, opacity: 0.2 },
      ],
      { duration: 600, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)' }
    );
    flight.onfinish = () => {
      gift.remove();
      cart.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
        { duration: 300, easing: 'ease-out' }
      );
    };
  });

  /* ---------- Start ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
    document.addEventListener('DOMContentLoaded', initHang);
  } else {
    initReveal();
    initHang();
  }
  document.addEventListener('shopify:section:load', initReveal);
  document.addEventListener('shopify:section:load', initHang);
})();
